// src/components/insights/AskInsights.js — "The Studio": the new Insights.
//
// Home: greeting → a two-line brief (every figure a tappable chip) → pinned
// answers (live tiles) → suggested questions → a floating ask bar ("/" to
// focus — ⌘K is already the admin-wide search palette, so it's left alone).
// Asking opens the conversation: what-it's-doing steps, then the answer card
// (number, chart, members, proof), then follow-ups. Pins, brief chips, card
// actions and clarifying choices run a measure directly — instant, no AI.
//
// Design: docs/ux/ask-insights-preview.html (Direction 1, owner-approved
// 2026-11-01). Plan + rules: docs/plans/insights-ai-plan.md.
// Module-scope components only; hover via useState.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useUser } from '@clerk/clerk-react';
import { askInsights, fetchInsightsHome, postInsightsHome, runInsightMeasure } from '../../services/askInsights';
import { ResultCard, ClarifyCard, CannotCard, Steps, Chip, DeltaPill, PinGlyph } from './InsightAnswer';
import { Sparkline, MiniBars } from './InsightCharts';
import { T, fmtParts } from './insightsFormat';

const STYLES = `
@keyframes momInsRise { from { opacity: 0; transform: translateY(12px) } to { opacity: 1; transform: none } }
@keyframes momInsFade { from { opacity: 0 } to { opacity: 1 } }
@keyframes momInsPulse { 0%,100% { box-shadow: 0 0 0 4px color-mix(in srgb, #7f9a7e 22%, transparent) } 50% { box-shadow: 0 0 0 7px color-mix(in srgb, #7f9a7e 6%, transparent) } }
@keyframes momInsShimmer { to { background-position: -200% 0 } }
`;

function useIsMobile() {
    const [m, setM] = useState(() => typeof window !== 'undefined' && !!window.matchMedia?.('(max-width: 768px)').matches);
    useEffect(() => {
        const mq = window.matchMedia?.('(max-width: 768px)');
        if (!mq) return undefined;
        const fn = (e) => setM(e.matches);
        if (mq.addEventListener) mq.addEventListener('change', fn); else mq.addListener(fn);
        return () => { if (mq.removeEventListener) mq.removeEventListener('change', fn); else mq.removeListener(fn); };
    }, []);
    return m;
}

const timeAgo = (ms) => {
    if (!ms) return '';
    const m = Math.round((Date.now() - ms) / 60000);
    if (m < 1) return 'just now';
    if (m < 60) return `${m} min ago`;
    return new Date(ms).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
};

function greetingWord() {
    const h = new Date().getHours();
    return h < 12 ? 'Good morning' : h < 17 ? 'Good afternoon' : 'Good evening';
}

const AskGlyph = ({ size = 16 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true"><path d="M4 19V10M10 19V5M16 19v-6M22 19H2" /></svg>
);

// ── Tile ──
function Tile({ pin, result, onOpen, onUnpin }) {
    const [h, setH] = useState(false);
    const tile = result && result.tile;
    const p = tile ? fmtParts(tile.value, tile.format) : null;
    const unavailable = !result || result.status === 'unavailable';
    return (
        <div onMouseEnter={() => setH(true)} onMouseLeave={() => setH(false)} onClick={onOpen} role="button" tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'Enter') onOpen(); }}
            style={{
                position: 'relative', cursor: 'pointer', borderRadius: 22, padding: '17px 17px 14px', minHeight: 158,
                background: T.glass, backdropFilter: 'blur(20px) saturate(1.4)', WebkitBackdropFilter: 'blur(20px) saturate(1.4)',
                border: '1px solid rgba(255,255,255,0.92)', boxShadow: h ? T.shLg : T.shMd,
                transform: h ? 'translateY(-3px)' : 'none', transition: 'transform .2s cubic-bezier(.2,.8,.2,1), box-shadow .2s',
                display: 'flex', flexDirection: 'column', outline: 'none',
            }}>
            <button type="button" aria-label="Unpin" onClick={(e) => { e.stopPropagation(); onUnpin(); }}
                style={{ position: 'absolute', top: 10, right: 10, width: 26, height: 26, borderRadius: '50%', border: 'none', background: h ? 'rgba(62,47,47,0.07)' : 'transparent', color: h ? T.soft : T.ghost, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all .15s' }}>
                {h ? <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.8" strokeLinecap="round" aria-hidden="true"><path d="M18 6L6 18M6 6l12 12" /></svg> : <PinGlyph size={12} />}
            </button>
            <div style={{ fontFamily: T.sans, fontSize: 12, color: T.soft, lineHeight: 1.35, paddingRight: 22, minHeight: 32 }}>{(tile && tile.label) || pin.title}</div>
            {unavailable ? (
                <div style={{ fontFamily: T.sans, fontSize: 12.5, color: T.faint, marginTop: 14, lineHeight: 1.45 }}>{result ? 'Needs Stripe history — available in production.' : 'Couldn\'t load.'}</div>
            ) : (
                <>
                    <div style={{ fontFamily: T.serif, fontWeight: 600, fontSize: 33, letterSpacing: '-0.02em', color: T.ink, marginTop: 8, lineHeight: 1.05 }}>
                        {p.main}<span style={{ fontSize: 16, color: T.faint, fontWeight: 500, marginLeft: 2 }}>{p.unit}</span>
                    </div>
                    <div style={{ marginTop: 8, minHeight: 21 }}>
                        {tile.delta != null ? <DeltaPill value={tile.delta} format={tile.deltaFormat} label={tile.deltaLabel} good={tile.deltaGoodWhenDown ? tile.delta <= 0 : tile.delta >= 0} />
                            : tile.sub ? <span style={{ fontFamily: T.sans, fontSize: 11.5, fontWeight: 700, color: T.soft, background: T.well, padding: '3px 9px', borderRadius: 99 }}>{tile.sub}</span> : null}
                    </div>
                    <div style={{ marginTop: 'auto', paddingTop: 10 }}>
                        {tile.spark ? <Sparkline values={tile.spark} /> : tile.progress != null ? (
                            <div style={{ height: 8, borderRadius: 99, background: T.hair, overflow: 'hidden', marginBottom: 6 }}>
                                <div style={{ height: '100%', width: `${Math.round(Math.max(0, Math.min(1, tile.progress)) * 100)}%`, borderRadius: 99, background: 'linear-gradient(90deg, var(--mom-primary), #e48aa6)' }} />
                            </div>
                        ) : tile.bars ? <MiniBars values={tile.bars} /> : tile.avatars ? <Avatars names={tile.avatars} extra={Math.max(0, tile.value - tile.avatars.length)} /> : null}
                    </div>
                </>
            )}
        </div>
    );
}

const AV = ['#c1848c', '#4f7c7a', '#b8913f', '#c05a7a'];
function Avatars({ names, extra }) {
    return (
        <div style={{ display: 'flex' }}>
            {names.map((n, i) => (
                <span key={n + i} style={{ width: 28, height: 28, borderRadius: '50%', marginLeft: i ? -8 : 0, border: '2px solid #fff', background: AV[i % AV.length], color: '#fff', fontFamily: T.sans, fontSize: 10, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {String(n).split(/\s+/).slice(0, 2).map((w) => (w[0] || '').toUpperCase()).join('')}
                </span>
            ))}
            {extra > 0 && <span style={{ height: 28, padding: '0 8px', borderRadius: 99, marginLeft: -8, border: '2px solid #fff', background: T.well, color: T.soft, fontFamily: T.sans, fontSize: 10.5, fontWeight: 700, display: 'flex', alignItems: 'center' }}>+{extra}</span>}
        </div>
    );
}

function BriefChip({ part, onRun }) {
    const [h, setH] = useState(false);
    const color = part.tone === 'up' ? T.good : part.tone === 'warn' ? 'var(--mom-primary-dark, #c05a7a)' : T.ink;
    return (
        <button type="button" onClick={() => onRun(part.run, part.chip)} onMouseEnter={() => setH(true)} onMouseLeave={() => setH(false)}
            style={{ font: 'inherit', fontFamily: T.sans, fontSize: 14, fontWeight: 700, padding: '1px 9px', borderRadius: 8, cursor: 'pointer', whiteSpace: 'nowrap', background: '#fff', color: h ? 'var(--mom-primary)' : color, border: `1px solid ${h ? 'color-mix(in srgb, var(--mom-primary) 40%, transparent)' : T.line}`, boxShadow: T.shSm, verticalAlign: '1px', transition: 'all .15s' }}>
            {part.chip}
        </button>
    );
}

// ── Ask bar ──
function AskBar({ value, onChange, onSubmit, busy, inputRef, compact }) {
    const [focus, setFocus] = useState(false);
    const [h, setH] = useState(false);
    return (
        <form onSubmit={(e) => { e.preventDefault(); if (value.trim() && !busy) onSubmit(value.trim()); }}
            style={{
                display: 'flex', alignItems: 'center', gap: 12, padding: compact ? '8px 8px 8px 14px' : '10px 10px 10px 18px', borderRadius: compact ? 20 : 22,
                background: 'rgba(255,255,255,0.94)', backdropFilter: 'blur(24px) saturate(1.5)', WebkitBackdropFilter: 'blur(24px) saturate(1.5)',
                border: `1px solid ${focus ? 'color-mix(in srgb, var(--mom-primary) 35%, transparent)' : 'rgba(255,255,255,0.95)'}`,
                boxShadow: `0 18px 50px rgba(62,47,47,${focus ? 0.2 : 0.15}), 0 2px 6px rgba(62,47,47,.05)${focus ? ', 0 0 0 4px color-mix(in srgb, var(--mom-primary) 10%, transparent)' : ''}`,
                transition: 'box-shadow .2s, border-color .2s',
            }}>
            <span style={{ flex: '0 0 auto', width: compact ? 28 : 30, height: compact ? 28 : 30, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'radial-gradient(circle at 30% 25%, #e48aa6, var(--mom-primary) 60%, var(--mom-primary-dark, #c05a7a))', boxShadow: '0 4px 12px color-mix(in srgb, var(--mom-primary) 35%, transparent)' }}>
                {busy ? <span style={{ width: 13, height: 13, border: '2px solid rgba(255,255,255,0.45)', borderTopColor: '#fff', borderRadius: '50%', animation: 'spin .8s linear infinite' }} /> : <AskGlyph size={compact ? 14 : 16} />}
            </span>
            <input ref={inputRef} value={value} onChange={(e) => onChange(e.target.value)} onFocus={() => setFocus(true)} onBlur={() => setFocus(false)}
                placeholder={compact ? 'Ask anything…' : 'Ask anything about members, workouts, classes or revenue…'}
                aria-label="Ask Insights a question" maxLength={600}
                style={{ flex: 1, minWidth: 0, border: 'none', outline: 'none', background: 'transparent', fontFamily: T.sans, fontSize: compact ? 15 : 15.5, color: T.ink }} />
            {!compact && !value && <span style={{ fontFamily: T.sans, fontSize: 11, color: T.faint, border: `1px solid ${T.line}`, borderRadius: 6, padding: '2px 6px', background: T.well }}>/</span>}
            <button type="submit" disabled={!value.trim() || busy} aria-label="Ask" onMouseEnter={() => setH(true)} onMouseLeave={() => setH(false)}
                style={{ flex: '0 0 auto', width: compact ? 36 : 40, height: compact ? 36 : 40, borderRadius: 14, border: 'none', cursor: value.trim() && !busy ? 'pointer' : 'default', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', background: value.trim() ? 'var(--mom-primary)' : 'var(--mom-charcoal)', opacity: !value.trim() || busy ? 0.75 : 1, transform: h && value.trim() ? 'scale(1.05)' : 'none', transition: 'all .15s' }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 12h14M13 6l6 6-6 6" /></svg>
            </button>
        </form>
    );
}

// ── One conversation turn ──
function Turn({ turn, onAsk, onRun, onPin, pinnedKeys, isMobile }) {
    const primary = turn.answer ? turn.results.find((r) => r.id === turn.answer.primary) || turn.results[0] : turn.results[0];
    const others = turn.results.filter((r) => r !== primary);
    const pinKey = (r) => `${r.measure}|${JSON.stringify(r.args || {})}`;
    return (
        <div data-turn={turn.id} style={{ marginBottom: 34 }}>
            <div style={{ display: 'flex', justifyContent: 'flex-end', margin: '4px 0 14px' }}>
                <span style={{ background: 'var(--mom-charcoal)', color: '#fff', fontFamily: T.sans, fontSize: 14.5, lineHeight: 1.45, padding: '11px 16px', borderRadius: '18px 18px 6px 18px', maxWidth: isMobile ? '88%' : '70%', boxShadow: T.shSm }}>{turn.q}</span>
            </div>
            <Steps steps={turn.steps} done={turn.done} />
            {!turn.done && !turn.results.length && !turn.clarify && !turn.cannot && !turn.error && (
                <div style={{ height: 140, borderRadius: 28, background: `linear-gradient(90deg, ${T.hair} 0%, rgba(255,255,255,0.9) 50%, ${T.hair} 100%)`, backgroundSize: '200% 100%', animation: 'momInsShimmer 1.4s linear infinite', opacity: 0.8 }} />
            )}
            {primary && (
                <ResultCard result={primary} sentence={turn.answer ? turn.answer.sentence : null} followups={turn.answer ? turn.answer.followups : (turn.done ? primary.followups : [])}
                    onAsk={onAsk} onRun={onRun} onPin={onPin} isPinned={pinnedKeys.has(pinKey(primary))} asOf={turn.asOf} />
            )}
            {!turn.answer && primary && !turn.done && turn.results.length > 0 && (
                <div style={{ fontFamily: T.sans, fontSize: 12.5, color: T.faint, margin: '10px 4px 0', display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ width: 11, height: 11, border: '2px solid #f0d6d4', borderTopColor: 'var(--mom-primary)', borderRadius: '50%', animation: 'spin .8s linear infinite' }} />Writing the answer…
                </div>
            )}
            {others.map((r) => (
                <div key={r.id} style={{ marginTop: 14 }}>
                    <ResultCard result={r} secondary onAsk={onAsk} onRun={onRun} onPin={onPin} isPinned={pinnedKeys.has(pinKey(r))} asOf={turn.asOf} />
                </div>
            ))}
            {turn.clarify && <ClarifyCard {...turn.clarify} onAsk={onAsk} onRun={onRun} />}
            {turn.cannot && <CannotCard {...turn.cannot} onAsk={onAsk} onRun={onRun} />}
            {turn.error && (
                <div style={{ fontFamily: T.sans, fontSize: 14, color: T.soft, background: 'rgba(255,255,255,0.7)', border: `1px solid ${T.line}`, borderRadius: 18, padding: '14px 18px' }}>
                    {turn.error} <button type="button" onClick={() => onAsk(turn.q)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, font: 'inherit', fontWeight: 600, color: 'var(--mom-primary)' }}>Try again</button>
                </div>
            )}
        </div>
    );
}

let _turnSeq = 0;
const newTurn = (q, extra = {}) => ({ id: `t${Date.now().toString(36)}${(_turnSeq += 1)}`, q, steps: [], results: [], answer: null, clarify: null, cannot: null, error: null, done: false, ...extra });

export default function AskInsights({ getToken, onOpenOld }) {
    const isMobile = useIsMobile();
    let firstName = '';
    try { firstName = useUser().user?.firstName || ''; } catch (_) { /* outside Clerk */ }

    const [home, setHome] = useState(null);
    const [homeErr, setHomeErr] = useState('');
    const [refreshing, setRefreshing] = useState(false);
    const [turns, setTurns] = useState([]);
    const [mode, setMode] = useState('home');
    const [input, setInput] = useState('');
    const [busy, setBusy] = useState(false);
    const inputRef = useRef(null);
    const topRef = useRef(null);

    const loadHome = useCallback(async () => {
        try { const h = await fetchInsightsHome(getToken); setHome(h); setHomeErr(''); return h; }
        catch (e) { setHomeErr(e.message || 'Couldn\'t load Insights.'); return null; }
    }, [getToken]);

    const refresh = useCallback(async () => {
        if (refreshing) return;
        setRefreshing(true);
        try { await postInsightsHome(getToken, { action: 'refresh' }); await loadHome(); } catch (_) { /* keep current data */ }
        setRefreshing(false);
    }, [getToken, loadHome, refreshing]);

    // Paint from the last snapshot immediately; if it's stale, rebuild quietly
    // in the background and swap the numbers in when it's done.
    useEffect(() => {
        let alive = true;
        (async () => {
            const h = await loadHome();
            if (alive && h && h.stale) {
                setRefreshing(true);
                try { await postInsightsHome(getToken, { action: 'refresh' }); if (alive) await loadHome(); } catch (_) { /* keep */ }
                if (alive) setRefreshing(false);
            }
        })();
        return () => { alive = false; };
    }, [getToken, loadHome]);

    // "/" focuses the ask bar (unless you're already typing somewhere).
    useEffect(() => {
        const onKey = (e) => {
            if (e.key !== '/' || e.metaKey || e.ctrlKey || e.altKey) return;
            const tag = (document.activeElement && document.activeElement.tagName) || '';
            if (/INPUT|TEXTAREA|SELECT/.test(tag) || document.activeElement?.isContentEditable) return;
            e.preventDefault(); inputRef.current?.focus();
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, []);

    const patchTurn = useCallback((id, fn) => setTurns((ts) => ts.map((t) => (t.id === id ? { ...t, ...fn(t) } : t))), []);

    const scrollToTurn = (id) => {
        setTimeout(() => {
            const el = document.querySelector(`[data-turn="${id}"]`);
            if (el) window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - (isMobile ? 76 : 24), behavior: 'smooth' });
        }, 60);
    };

    const historyFrom = (ts) => ts.filter((t) => t.done).map((t) => {
        const r = t.answer ? t.results.find((x) => x.id === t.answer.primary) || t.results[0] : t.results[0];
        return { q: t.q, a: r ? { measure: r.measure, args: r.args } : t.clarify ? { clarify: t.clarify.question } : {} };
    });

    const ask = useCallback(async (question) => {
        if (!question || busy) return;
        const t = newTurn(question);
        const history = historyFrom(turns);
        setTurns((ts) => [...ts, t]);
        setMode('thread');
        setInput('');
        setBusy(true);
        scrollToTurn(t.id);
        try {
            await askInsights(getToken, { question, history }, (ev) => {
                if (ev.type === 'step') patchTurn(t.id, (x) => {
                    // A "done" step replaces the matching running step.
                    const steps = x.steps.slice();
                    if (ev.state === 'done') {
                        const base = ev.text.split(' · ')[0];
                        const i = steps.map((s) => s.text).lastIndexOf(base);
                        if (i >= 0) { steps[i] = { text: ev.text, state: 'done' }; return { steps }; }
                    }
                    const prev = steps[steps.length - 1];
                    if (prev && prev.state === 'run' && ev.state !== 'note') steps[steps.length - 1] = { ...prev, state: 'done' };
                    steps.push({ text: ev.text, state: ev.state || 'run' });
                    return { steps };
                });
                else if (ev.type === 'result') patchTurn(t.id, (x) => ({ results: [...x.results, ev.result] }));
                else if (ev.type === 'answer') patchTurn(t.id, () => ({ answer: ev }));
                else if (ev.type === 'clarify') patchTurn(t.id, () => ({ clarify: { question: ev.question, options: ev.options, choices: ev.choices } }));
                else if (ev.type === 'cannot') patchTurn(t.id, () => ({ cannot: { reason: ev.reason, closest: ev.closest, suggestions: ev.suggestions } }));
                else if (ev.type === 'error') patchTurn(t.id, () => ({ error: ev.message }));
                else if (ev.type === 'done') patchTurn(t.id, (x) => ({ done: true, asOf: ev.asOf, steps: x.steps.map((s) => (s.state === 'run' ? { ...s, state: 'done' } : s)) }));
            });
        } catch (e) {
            patchTurn(t.id, () => ({ error: e.message || 'Something went wrong.' }));
        }
        patchTurn(t.id, (x) => ({ done: true, steps: x.steps.map((s) => (s.state === 'run' ? { ...s, state: 'done' } : s)) }));
        setBusy(false);
    }, [busy, turns, getToken, patchTurn]);

    // A measure run straight from a tile / chip / action / choice — no AI.
    const run = useCallback(async (spec, label) => {
        if (!spec || !spec.measure) return;
        const t = newTurn(label || 'Open', { steps: [{ text: 'Calculating from your data', state: 'run' }] });
        setTurns((ts) => [...ts, t]);
        setMode('thread');
        scrollToTurn(t.id);
        try {
            const { result, asOf } = await runInsightMeasure(getToken, spec);
            if (result.status === 'choose') patchTurn(t.id, () => ({ clarify: { question: result.lede, choices: result.choices }, steps: [], done: true }));
            else patchTurn(t.id, () => ({ results: [result], answer: { sentence: null, primary: result.id, followups: result.followups || [] }, steps: [], done: true, asOf }));
        } catch (e) {
            patchTurn(t.id, () => ({ error: e.message || 'Couldn\'t run that.', steps: [], done: true }));
        }
    }, [getToken, patchTurn]);

    const pinnedKeys = useMemo(() => new Set((home?.pins || []).map(({ pin }) => `${pin.measure}|${JSON.stringify(pin.args || {})}`)), [home]);

    const pin = useCallback(async (result) => {
        try {
            const { pins } = await postInsightsHome(getToken, { action: 'pin', pin: { measure: result.measure, args: result.args || {}, title: result.title } });
            setHome((h) => {
                if (!h) return h;
                const have = new Set(h.pins.map((p) => p.pin.id));
                const added = pins.filter((p) => !have.has(p.id)).map((p) => ({ pin: p, result }));
                return { ...h, pins: [...h.pins, ...added] };
            });
        } catch (_) { /* pin is a convenience — fail quietly */ }
    }, [getToken]);

    const unpin = useCallback(async (id) => {
        setHome((h) => (h ? { ...h, pins: h.pins.filter((p) => p.pin.id !== id) } : h));
        try { await postInsightsHome(getToken, { action: 'unpin', id }); } catch (_) { loadHome(); }
    }, [getToken, loadHome]);

    const goHome = () => { setMode('home'); setTimeout(() => topRef.current && window.scrollTo({ top: topRef.current.getBoundingClientRect().top + window.scrollY - 90, behavior: 'smooth' }), 30); };

    const today = new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
    const fresh = home ? (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontFamily: T.sans, fontSize: 11.5, color: T.soft }}>
            <i style={{ width: 7, height: 7, borderRadius: '50%', background: refreshing ? '#d8b26a' : '#7f9a7e', animation: refreshing ? 'momInsPulse 1.2s ease infinite' : 'none' }} />
            {refreshing ? 'Updating…' : `Data as of ${timeAgo(home.asOf)}`}
            {!refreshing && <button type="button" onClick={refresh} style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', font: 'inherit', fontWeight: 600, color: 'var(--mom-primary)' }}>Refresh</button>}
        </span>
    ) : null;

    return (
        <div ref={topRef} style={{ position: 'relative' }}>
            <style>{STYLES}</style>
            <div style={{
                position: 'relative', borderRadius: isMobile ? 24 : 30, overflow: 'visible',
                background: 'radial-gradient(900px 420px at 92% -8%, color-mix(in srgb, #f0d6d4 70%, transparent), transparent 60%), radial-gradient(700px 400px at -10% 105%, color-mix(in srgb, #f3e6da 80%, transparent), transparent 60%), var(--mom-cream)',
                border: '1px solid rgba(255,255,255,0.85)', boxShadow: '0 20px 60px rgba(62,47,47,.08)',
            }}>
                {/* top bar */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', padding: isMobile ? '14px 18px' : '15px 26px', borderBottom: `1px solid ${T.hair}`, background: 'rgba(255,255,255,0.45)', borderRadius: isMobile ? '24px 24px 0 0' : '30px 30px 0 0' }}>
                    {mode === 'thread' ? (
                        <button type="button" onClick={goHome} style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontFamily: T.sans, fontSize: 13, fontWeight: 600, color: T.ink, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" aria-hidden="true"><path d="M15 18l-6-6 6-6" /></svg>Insights home
                        </button>
                    ) : <span style={{ fontFamily: T.sans, fontSize: 12.5, color: T.faint }}>Insights</span>}
                    <span style={{ flex: 1 }} />
                    {fresh}
                    {onOpenOld && <Chip size="sm" onClick={onOpenOld} style={{ padding: '6px 12px', fontWeight: 600 }}>Insights (old)</Chip>}
                </div>

                <div style={{ padding: isMobile ? '24px 18px 30px' : '32px 36px 40px' }}>
                    {mode === 'home' ? (
                        <>
                            <div style={{ fontFamily: T.serif, fontWeight: 500, fontSize: isMobile ? 27 : 34, letterSpacing: '-0.01em', color: T.ink, lineHeight: 1.15 }}>
                                {greetingWord()}{firstName ? ',' : '.'}{firstName && <> <em style={{ color: 'var(--mom-primary)' }}>{firstName}.</em></>}
                            </div>
                            <div style={{ fontFamily: T.sans, fontSize: 13.5, color: T.soft, marginTop: 6 }}>{today} · here's where things stand</div>

                            {homeErr && <p style={{ fontFamily: T.sans, fontSize: 14, color: T.bad, marginTop: 18 }}>{homeErr} <button type="button" onClick={loadHome} style={{ background: 'none', border: 'none', cursor: 'pointer', font: 'inherit', fontWeight: 600, color: 'var(--mom-primary)', padding: 0 }}>Try again</button></p>}

                            {!home && !homeErr && (
                                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 22, fontFamily: T.sans, fontSize: 13.5, color: T.soft }}>
                                    <span style={{ width: 16, height: 16, border: '2px solid #f0d6d4', borderTopColor: 'var(--mom-primary)', borderRadius: '50%', animation: 'spin .8s linear infinite' }} />
                                    Gathering your numbers… the first time can take a few seconds.
                                </div>
                            )}

                            {home && home.brief && (
                                <p style={{ marginTop: 16, maxWidth: 760, fontFamily: T.serif, fontSize: isMobile ? 16 : 17.5, lineHeight: 1.75, color: T.ink }}>
                                    {home.brief.map((part, i) => (part.chip ? <BriefChip key={i} part={part} onRun={run} /> : <span key={i}>{part.t}</span>))}
                                </p>
                            )}

                            {home && (
                                <>
                                    <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', margin: '30px 0 12px' }}>
                                        <span style={{ fontFamily: T.sans, fontSize: 11, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: T.faint }}>Pinned</span>
                                        <span style={{ fontFamily: T.sans, fontSize: 11.5, color: T.faint }}>Pin any answer to keep it here</span>
                                    </div>
                                    {home.pins.length ? (
                                        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(auto-fill, minmax(190px, 1fr))', gap: isMobile ? 10 : 14 }}>
                                            {home.pins.map(({ pin: p, result }) => (
                                                <Tile key={p.id} pin={p} result={result} onOpen={() => run({ measure: p.measure, args: p.args }, (result && result.title) || p.title)} onUnpin={() => unpin(p.id)} />
                                            ))}
                                        </div>
                                    ) : (
                                        <p style={{ fontFamily: T.sans, fontSize: 13.5, color: T.soft, background: 'rgba(255,255,255,0.55)', border: `1px dashed ${T.line}`, borderRadius: 18, padding: '18px 20px' }}>Nothing pinned yet. Ask a question, then tap <b>Pin to home</b> on any answer you want to keep an eye on.</p>
                                    )}

                                    <div style={{ fontFamily: T.sans, fontSize: 11, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: T.faint, margin: '34px 0 12px' }}>Try asking</div>
                                    <div style={{ display: 'flex', gap: 9, flexWrap: 'wrap' }}>
                                        {(home.suggestions || []).map((s) => <Chip key={s.q} tag={isMobile ? null : s.tag} onClick={() => ask(s.q)}>{s.q}</Chip>)}
                                    </div>
                                </>
                            )}

                            {turns.length > 0 && (
                                <button type="button" onClick={() => { setMode('thread'); scrollToTurn(turns[turns.length - 1].id); }} style={{ marginTop: 26, background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontFamily: T.sans, fontSize: 13, fontWeight: 600, color: 'var(--mom-primary)' }}>
                                    Back to your conversation ({turns.length}) →
                                </button>
                            )}
                        </>
                    ) : (
                        <>
                            {turns.map((t) => <Turn key={t.id} turn={t} onAsk={ask} onRun={run} onPin={pin} pinnedKeys={pinnedKeys} isMobile={isMobile} />)}
                            <div style={{ display: 'flex', justifyContent: 'center', gap: 10, marginTop: 4 }}>
                                <Chip size="sm" onClick={() => setTurns([])} style={{ color: T.soft }}>Clear conversation</Chip>
                            </div>
                        </>
                    )}
                </div>

                {/* Floating ask bar — sticks to the bottom of the screen while you scroll. */}
                <div style={{ position: 'sticky', bottom: isMobile ? 'calc(76px + env(safe-area-inset-bottom, 0px))' : 22, zIndex: 30, padding: isMobile ? '0 10px 14px' : '0 0 22px', display: 'flex', justifyContent: 'center', pointerEvents: 'none' }}>
                    <div style={{ width: isMobile ? '100%' : 'min(720px, calc(100% - 60px))', pointerEvents: 'auto' }}>
                        <AskBar value={input} onChange={setInput} onSubmit={ask} busy={busy} inputRef={inputRef} compact={isMobile} />
                        {!isMobile && mode === 'home' && <div style={{ textAlign: 'center', fontFamily: T.sans, fontSize: 11.5, color: T.faint, marginTop: 9 }}>Answers come only from your data. If something isn't in it, it will say so.</div>}
                    </div>
                </div>
            </div>
        </div>
    );
}
